<template>
  <ReportCard :title="$t('report.client_types_chart_title')">
    <div class="flex items-center justify-end mb-2">
      <TogglePill
        :model-value="chartMode === 'absolute'"
        @update:model-value="$emit('update:chartMode', $event ? 'absolute' : 'percent')"
      >
        <template #icon>
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 12h10M3 9v3M6 6v6M9 3v9M12 1v11" />
          </svg>
        </template>
        {{ $t('chart.mode_absolute') }}
      </TogglePill>
    </div>
    <div class="ct-legend flex items-center justify-center gap-3">
      <span v-for="type in clientTypeKeys" :key="'rev-legend-' + type"
        class="flex items-center gap-1.5">
        <span class="w-2 h-2 rounded-full"
          :style="{ backgroundColor: clientTypeColor(type) }"></span>
        <span class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {{ type }}
        </span>
      </span>
    </div>
    <div class="relative">
      <div ref="chartEl" class="w-full h-64"></div>
      <div v-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg transition-opacity">
        <Loading />
      </div>
    </div>
  </ReportCard>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts'
import type { SalesByClientTypesResponse, TaxDataKey } from '../../responses'
import { useAppSettingsStore } from '../../stores/AppSettingsStore'
import { formatCurrencyForCenter, formatAmount } from '../../utils/Languages'
import { clientTypeColor } from '../../utils/ClientTypeColors'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import TogglePill from '../ui/TogglePill.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: SalesByClientTypesResponse
  chartMode: 'percent' | 'absolute'
  taxKey: TaxDataKey
  centerId: number
  formatXLabel: (dateString: string) => string
  loading: boolean
}>()

defineEmits<{
  'update:chartMode': [value: 'percent' | 'absolute']
}>()

const appSettingsStore = useAppSettingsStore()
const centerObj = computed(() => appSettingsStore.getCenter(props.centerId))

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()

/** Types with current-interval data */
const clientTypeKeys = computed<string[]>(() => {
  if (!props.response?.client_types) return []
  return Object.keys(props.response.client_types).filter(type => {
    const data = props.response.client_types[type]
    if (!data?.intervals?.length) return false
    return data.intervals.some(i =>
      i.data.period.tax_data.excluded_tax !== 0 || i.data.period.tax_data.included_tax !== 0,
    )
  })
})

// --- Helpers ---
const isDark = useIsDarkMode()

function getClientTypeIntervals(type: string): number[] {
  const data = props.response?.client_types[type]
  if (!data?.intervals) return []
  return data.intervals.map(i => i.data.period.tax_data[props.taxKey])
}

function getClientTypeXAxis(): string[] {
  const firstType = clientTypeKeys.value[0]
  if (!firstType) return []
  const data = props.response?.client_types[firstType]
  if (!data?.intervals) return []
  return data.intervals.map(i => props.formatXLabel(i.date))
}

// --- Build chart ---
function buildChartOptions(): echarts.EChartsOption {
  const center = centerObj.value
  const xAxisData = getClientTypeXAxis()
  const types = clientTypeKeys.value

  const rawByType: Record<string, number[]> = {}
  for (const type of types) {
    rawByType[type] = getClientTypeIntervals(type)
  }

  const intervalCount = xAxisData.length
  const totals: number[] = Array(intervalCount).fill(0)
  for (const type of types) {
    for (let i = 0; i < intervalCount; i++) {
      totals[i] += rawByType[type]?.[i] || 0
    }
  }

  const isPercent = props.chartMode === 'percent'

  const barSeries: echarts.BarSeriesOption[] = types.map((type, idx) => {
    const data = isPercent
      ? Array(intervalCount).fill(0).map((_, i) =>
          totals[i] > 0 ? ((rawByType[type]?.[i] || 0) / totals[i]) * 100 : 0,
        )
      : rawByType[type] || []
    return {
      type: 'bar',
      name: type,
      stack: 'total',
      data,
      itemStyle: {
        color: clientTypeColor(type),
        borderRadius: idx === types.length - 1 ? [2, 2, 0, 0] : undefined,
      },
      emphasis: { focus: 'series' },
    }
  })

  return {
    grid: { left: '2%', right: '2%', bottom: '12%', top: '8%', containLabel: true },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.15)' : 'rgba(156,163,175,0.3)' } },
      axisTick: { show: false },
      axisLabel: { color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11 },
    },
    yAxis: isPercent
      ? {
          type: 'value',
          min: 0,
          max: 100,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.4)', type: 'dashed' } },
          axisLabel: {
            color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)',
            fontSize: 11,
            formatter: (v: number) => v + '%',
          },
        }
      : {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitNumber: 4,
          splitLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.4)', type: 'dashed' } },
          axisLabel: {
            color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)',
            fontSize: 10,
            formatter: (v: number) => {
              if (v === 0) return `0 ${center?.currencySymbol || '\u20AC'}`
              return formatAmount(v, { center: center ?? undefined, compact: true })
            },
          },
        },
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark.value ? 'rgba(40,39,39,0.95)' : 'rgba(255,255,255,0.95)',
      borderColor: isDark.value ? 'rgba(255,255,255,0.1)' : 'rgba(229,231,235,0.8)',
      borderWidth: 1,
      borderRadius: 8,
      textStyle: { color: isDark.value ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      confine: true,
      formatter: (params: any) => {
        if (!Array.isArray(params) || params.length === 0) return ''
        const idx = params[0].dataIndex
        let html = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`
        for (const p of params) {
          if (p.value == null) continue
          const raw = rawByType[p.seriesName]?.[idx] || 0
          const pct = totals[idx] > 0 ? ((raw / totals[idx]) * 100).toFixed(1) : '0.0'
          html += `<div style="display:flex;align-items:center;gap:6px;margin-top:2px">`
          html += `${p.marker}<span>${p.seriesName}</span>`
          html += `<span style="margin-left:auto;font-weight:600">${formatCurrencyForCenter(raw, center)}</span>`
          html += `<span style="color:rgba(128,128,128,0.8)">(${pct}%)</span>`
          html += `</div>`
        }
        return html
      },
    },
    dataZoom: [{ type: 'inside', xAxisIndex: [0] }],
    series: [...barSeries],
  }
}

// --- Init / update / dispose ---
function initChart() {
  if (!chartEl.value) return
  chartInstance.value = echarts.init(chartEl.value)
  chartInstance.value.setOption(buildChartOptions())
}

function refreshChart() {
  if (chartInstance.value) {
    chartInstance.value.setOption(buildChartOptions(), true)
  } else {
    initChart()
  }
}

watch([clientTypeKeys, () => props.taxKey, () => props.chartMode], () => {
  refreshChart()
}, { deep: true })

// When response changes (aggregation switch), rebuild chart
watch(() => props.response, async () => {
  await nextTick()
  refreshChart()
})

watch(isDark, () => refreshChart())

// Initial mount — defer to allow DOM to settle after transitions
watch(chartEl, async (el) => {
  if (el && !chartInstance.value) {
    await nextTick()
    setTimeout(initChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

/** Public: allow parent to force a full re-init (e.g. after aggregation fetch). */
defineExpose({ refreshChart })
</script>
